import React, { ReactNode } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Trans } from "react-i18next";
import ArrowLeft from "@icons/myicons/arrow-left.svg?react";
import { ScrollableWithGradients } from "@components/Gradient/ScrollableWithGradients";
import { TypographyWithInfoIcon } from "@components";

export type ClassificationLegendBreadcrumb = {
  name: string;
  onClick: () => void;
};

export interface ClassificationLegendLayoutProps {
  breadcrumbs: ClassificationLegendBreadcrumb[];
  chartTitle: string;
  chartTitleTooltipKey?: string;
  children: ReactNode;
  className?: string;
}

export const LegendLayout = ({
  breadcrumbs,
  chartTitle,
  chartTitleTooltipKey,
  children,
  className,
}: ClassificationLegendLayoutProps) => {
  const theme = useTheme();
  const isDeepDive = breadcrumbs.length > 0;

  return (
    <Box className={className} width="100%">
      {isDeepDive && (
        <Box
          display="flex"
          alignItems="center"
          color="text.secondary"
          gap={1}
          mb={1.5}
          flexWrap="wrap"
        >
          {breadcrumbs.map((crumb) => (
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              key={`crumb-${crumb.name}`}
              onClick={crumb.onClick}
              sx={{
                cursor: "pointer",
              }}
            >
              <Box display="flex">
                <ArrowLeft width={24} height={24} />
              </Box>
              <Typography
                variant="subtitle2"
                color={theme.palette.text.secondary}
              >
                {crumb.name}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        mb={2.5}
      >
        {chartTitle ? (
          <TypographyWithInfoIcon
            tooltipText={
              chartTitleTooltipKey && !isDeepDive ? (
                <Typography variant="body3" color="text.onTooltipSecondarySub">
                  <Trans
                    i18nKey={chartTitleTooltipKey}
                    components={{
                      "<br/>": <br />,
                    }}
                  />
                </Typography>
              ) : (
                ""
              )
            }
            component="span"
            variant="subtitle2"
            iconSize={22}
          >
            {chartTitle}
          </TypographyWithInfoIcon>
        ) : (
          <span />
        )}
      </Box>
      <ScrollableWithGradients
        sx={{
          maxHeight: { xs: "50vh", md: "60vh" },
          borderTop: `1px solid ${theme.palette.border.secondary}`,
        }}
        recalculateDeps={[children]}
      >
        {children}
      </ScrollableWithGradients>
    </Box>
  );
};
